import { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Defaults must mirror lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG
const DEFAULTS = {
    filtroStatus: 'Vago/Disponível',
    limiteBusca: 15,
    maxSugestoes: 3,
    maxTentativasRelax: 3,
    rodadasParaLinkCompleto: 2,
    maxImoveisLink: 20,
    propertyUrlPattern: 'https://www.psimobiliaria.com.br/imovel/[FINALIDADE]/[ID]',
    priceRanking: 'cheapest',
    suggestionStyle: 'detailed_balloons',
};

// PRICE_RANKING_OPCOES e SUGGESTION_STYLE_OPCOES precisam de t(), então viraram
// useMemo dentro do componente (ver abaixo) em vez de const de módulo — nenhum
// outro arquivo importa essas duas constantes, então mover pra dentro é seguro.

// Clamp a numeric input to [min, max]; returns undefined when empty so the
// field keeps using the default from the agent runtime.
const coerceInt = (value, min, max) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.trunc(n);
    return Math.max(min, Math.min(max, i));
};

export default function BuscaCatalogoSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const busca = aiAgent?.config?.module?.realstate?.busca || {};

    const updateBusca = (patch) => {
        changeModuleConfig({
            busca: {
                ...busca,
                ...patch,
            },
        });
    };

    const priceRankingOpcoes = useMemo(
        () => [
            { value: 'cheapest', label: t('aiAgents.realstate.buscaCatalogo.priceRankingCheapest', 'Mais baratos primeiro') },
            { value: 'closest_to_budget', label: t('aiAgents.realstate.buscaCatalogo.priceRankingClosestToBudget', 'Próximos do orçamento do cliente') },
            { value: 'largest_area', label: t('aiAgents.realstate.buscaCatalogo.priceRankingLargestArea', 'Maior área dentro do orçamento') },
        ],
        [t]
    );

    const suggestionStyleOpcoes = useMemo(
        () => [
            { value: 'detailed_balloons', label: t('aiAgents.realstate.buscaCatalogo.suggestionStyleDetailedBalloons', 'Cards com ícones (1 mensagem por imóvel)') },
            { value: 'compact_inline', label: t('aiAgents.realstate.buscaCatalogo.suggestionStyleCompactInline', 'Lista compacta inline') },
        ],
        [t]
    );

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:magnify" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.realstate.buscaCatalogo.title', 'Busca & Catálogo')}</CardTitle>
                        <CardDescription>{t('aiAgents.realstate.buscaCatalogo.subtitle', 'Parâmetros operacionais da busca de imóveis e do fluxo de sugestões.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                {/* <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>Status do imóvel para considerar disponível</Label>
                    <Input
                        value={busca.filtroStatus ?? ''}
                        onChange={(e) => updateBusca({ filtroStatus: e.target.value })}
                        placeholder={DEFAULTS.filtroStatus}
                        className={INPUT_CLS}
                    />
                    <p className={HINT_CLS}>Valor exato do campo <code>status</code> na coleção de imóveis. Padrão: <code>{DEFAULTS.filtroStatus}</code>.</p>
                </div> */}

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.propertyUrlLabel', 'URL pública do imóvel')}</Label>
                    <Input
                        value={busca.propertyUrlPattern ?? ''}
                        onChange={(e) => updateBusca({ propertyUrlPattern: e.target.value })}
                        placeholder={DEFAULTS.propertyUrlPattern}
                        className={INPUT_CLS}
                    />
                    <p className={HINT_CLS}>
                        <Trans
                            t={t}
                            i18nKey="aiAgents.realstate.buscaCatalogo.propertyUrlHint"
                            defaults="Padrão de link enviado ao cliente quando a Lais apresenta um imóvel. Use os placeholders <code1>[codigo do imovel]</code1> (ou <code2>[ID]</code2>) e, se necessário, <code3>[FINALIDADE]</code3> (vira <code4>aluguel</code4> ou <code5>venda</code5>). Ex.: <code6>https://www.psimobiliaria.com.br/imovel/interno/[codigo do imovel]</code6>."
                            components={{
                                code1: <code />,
                                code2: <code />,
                                code3: <code />,
                                code4: <code />,
                                code5: <code />,
                                code6: <code />,
                            }}
                        />
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.dbLimitLabel', 'Limite da busca no banco')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={100}
                            value={busca.limiteBusca ?? ''}
                            onChange={(e) => updateBusca({ limiteBusca: coerceInt(e.target.value, 1, 100) })}
                            placeholder={String(DEFAULTS.limiteBusca)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.realstate.buscaCatalogo.dbLimitHint', 'Máximo de imóveis retornados do MongoDB por query. Padrão: {{value}}.', { value: DEFAULTS.limiteBusca })}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxSugestoesLabel', 'Sugestões por resposta')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxSugestoes ?? ''}
                            onChange={(e) => updateBusca({ maxSugestoes: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxSugestoes)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxSugestoesHint', 'Quantos imóveis a Lais apresenta ao cliente por rodada. Padrão: {{value}}.', { value: DEFAULTS.maxSugestoes })}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxTentativasRelaxLabel', 'Tentativas de relaxamento')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxTentativasRelax ?? ''}
                            onChange={(e) => updateBusca({ maxTentativasRelax: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxTentativasRelax)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxTentativasRelaxHint', 'Quantas vezes a Lais tenta relaxar filtros antes de admitir que não achou nada. Padrão: {{value}}.', { value: DEFAULTS.maxTentativasRelax })}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.rodadasLinkLabel', 'Rodadas até link completo')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.rodadasParaLinkCompleto ?? ''}
                            onChange={(e) => updateBusca({ rodadasParaLinkCompleto: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.rodadasParaLinkCompleto)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.realstate.buscaCatalogo.rodadasLinkHint', 'Depois de N rodadas de sugestões, a Lais envia um link com todos os imóveis. Padrão: {{value}}.', { value: DEFAULTS.rodadasParaLinkCompleto })}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxImoveisLinkLabel', 'Máximo de imóveis no link')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={200}
                            value={busca.maxImoveisLink ?? ''}
                            onChange={(e) => updateBusca({ maxImoveisLink: coerceInt(e.target.value, 1, 200) })}
                            placeholder={String(DEFAULTS.maxImoveisLink)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>{t('aiAgents.realstate.buscaCatalogo.maxImoveisLinkHint', 'Quantos imóveis no máximo entram no link completo enviado ao cliente. Padrão: {{value}}.', { value: DEFAULTS.maxImoveisLink })}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.priceRankingLabel', 'Como ordenar imóveis sugeridos')}</Label>
                        <Select
                            value={busca.priceRanking || DEFAULTS.priceRanking}
                            onValueChange={(value) => updateBusca({ priceRanking: value })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder={t('aiAgents.realstate.buscaCatalogo.selectPlaceholder', 'Selecione')} />
                            </SelectTrigger>
                            <SelectContent>
                                {priceRankingOpcoes.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className={HINT_CLS}>
                            <Trans
                                t={t}
                                i18nKey="aiAgents.realstate.buscaCatalogo.priceRankingHint"
                                defaults="Define a ordenação dos imóveis na busca. <strong1>Próximos do orçamento</strong1> mostra opções perto do teto que o cliente informou (ex.: cliente disse R$ 2.500 → mostra R$ 2.500/2.200/2.200, não R$ 350)."
                                components={{ strong1: <strong /> }}
                            />
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.buscaCatalogo.suggestionStyleLabel', 'Estilo das mensagens de sugestão')}</Label>
                        <Select
                            value={busca.suggestionStyle || DEFAULTS.suggestionStyle}
                            onValueChange={(value) => updateBusca({ suggestionStyle: value })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder={t('aiAgents.realstate.buscaCatalogo.selectPlaceholder', 'Selecione')} />
                            </SelectTrigger>
                            <SelectContent>
                                {suggestionStyleOpcoes.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className={HINT_CLS}>
                            <Trans
                                t={t}
                                i18nKey="aiAgents.realstate.buscaCatalogo.suggestionStyleHint"
                                defaults='<strong1>Cards</strong1>: bolha separada por imóvel com 5 linhas (preço, localização, área, banheiros, destaques). <strong2>Lista compacta</strong2>: uma linha por imóvel com link inline (estilo "Loja de 45m² por R$ 2.500: link").'
                                components={{ strong1: <strong />, strong2: <strong /> }}
                            />
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
